import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/legal.css';

const CONTACT_EMAIL = 'hello@covo.app';
const APP_NAME = 'Covo';
const EFFECTIVE_DATE = 'May 6, 2025';

export default function Privacy() {
  const navigate = useNavigate();
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <span className="legal-logo" onClick={() => navigate('/')}>COVO</span>
        <button className="legal-back" onClick={() => navigate('/')}>← Back</button>
      </nav>

      <div className="legal-body">
        <h1>Privacy Policy</h1>
        <p className="legal-date">Effective date: {EFFECTIVE_DATE}</p>

        <p>
          {APP_NAME} ("we", "us", or "our") is a social coordination app that helps you plan activities with your friends.
          This Privacy Policy explains what information we collect, how we use it, and your choices.
        </p>

        <h2>1. Information we collect</h2>

        <h3>Account information</h3>
        <p>When you create an account we collect:</p>
        <ul>
          <li>Your email address or phone number (used as your login identifier)</li>
          <li>Your display name</li>
          <li>A password (stored as a one-way hash — we never see it in plain text)</li>
        </ul>

        <h3>Profile information you choose to provide</h3>
        <ul>
          <li>A short bio</li>
          <li>Your Instagram and/or Twitter/X handle</li>
        </ul>

        <h3>Activity and social data</h3>
        <ul>
          <li>Activities you create, edit, or RSVP to</li>
          <li>Messages you send in activity chats or direct messages</li>
          <li>Friend connections and Close Circle memberships you establish</li>
          <li>Reactions and comments you leave on activities</li>
        </ul>

        <h3>Calendar data (optional)</h3>
        <p>
          If you choose to connect Google Calendar, we request read-only access to your calendar events
          via the Google Calendar API. We store a copy of your upcoming events (from 7 days ago to
          60 days ahead) to display them alongside your Covo plans. This data is:
        </p>
        <ul>
          <li>Private to you — it is never shared with other users or shown in the social feed</li>
          <li>Used solely to display your own schedule within the app</li>
          <li>Deletable at any time by disconnecting the calendar from your settings</li>
        </ul>
        <p>
          Covo's use of information received from Google APIs will adhere to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <p>
          If you connect a calendar via an ICS URL or uploaded .ics file, we store the event data
          in the same private, user-only manner.
        </p>

        <h3>Technical information</h3>
        <p>
          We collect standard server logs (IP addresses, browser type, pages visited) for security
          and debugging purposes. We do not use third-party analytics or advertising trackers.
        </p>

        <h2>2. How we use your information</h2>
        <ul>
          <li>To create and maintain your account</li>
          <li>To display your profile to friends you connect with</li>
          <li>To power the activity feed, RSVP system, and messaging features</li>
          <li>To show your calendar events privately within the app</li>
          <li>To send notifications about activity updates, connection requests, and messages</li>
          <li>To respond to support requests</li>
          <li>To detect and prevent fraud or abuse</li>
        </ul>

        <h2>3. Information we do not collect or sell</h2>
        <ul>
          <li>We do not sell, rent, or trade your personal information to any third party</li>
          <li>We do not serve ads or use your data for advertising profiling</li>
          <li>We do not share your contact list or calendar data with other users or external services</li>
          <li>We do not track your location</li>
        </ul>

        <h2>4. Information sharing</h2>
        <p>
          We share your information only in the following limited circumstances:
        </p>
        <ul>
          <li>
            <strong>With friends you connect with:</strong> your display name, bio, social handles,
            and activities you make visible to connections
          </li>
          <li>
            <strong>With Google:</strong> when you connect Google Calendar, we exchange OAuth tokens
            with Google to read your calendar. We do not share your Covo data with Google.
          </li>
          <li>
            <strong>For legal reasons:</strong> if required by law, court order, or to protect the
            safety of our users
          </li>
          <li>
            <strong>Infrastructure providers:</strong> our hosting provider (Fly.io for the backend,
            Vercel for the frontend) processes your data on our behalf and does not use it for any
            other purpose
          </li>
        </ul>

        <h2>5. Data retention</h2>
        <p>
          We keep your data for as long as your account is active. You can delete your account at any
          time by emailing us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Upon
          deletion, we permanently remove your account, profile, activities, messages, and connected
          calendar data within 30 days.
        </p>

        <h2>6. Security</h2>
        <p>
          Passwords are hashed using bcrypt and are never stored in plain text. Data is transmitted
          over HTTPS. OAuth tokens for Google Calendar are stored encrypted at rest. We follow
          reasonable industry practices to protect your information, but no system is perfectly secure.
        </p>

        <h2>7. Children's privacy</h2>
        <p>
          Covo is not directed at children under 13. We do not knowingly collect personal information
          from children under 13. If you believe a child has created an account, contact us and we
          will delete it promptly.
        </p>

        <h2>8. Your rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate information in your profile (via the app)</li>
          <li>Delete your account and all associated data</li>
          <li>Disconnect Google Calendar and have those stored events removed</li>
          <li>Opt out of non-essential communications</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>9. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will update the
          effective date above. Continued use of Covo after changes constitutes acceptance of the
          updated policy.
        </p>

        <h2>10. Contact us</h2>
        <p>
          Questions about this policy? Email us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </div>

      <footer className="legal-footer">
        <span className="legal-footer-logo">COVO</span>
        <div className="legal-footer-links">
          <button onClick={() => navigate('/privacy')}>Privacy Policy</button>
          <button onClick={() => navigate('/terms')}>Terms of Service</button>
        </div>
      </footer>
    </div>
  );
}
